import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Building2,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  CreditCard,
  DollarSign,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  Users,
  Wifi,
  WifiOff,
  Shield,
  AlertCircle,
  Info,
  ExternalLink,
  ArrowRight
} from "lucide-react";
import PageHeader from "../../components/PageHeader";
import StatCard from "../../components/StatCard";
import LoadingScreen from "../../components/LoadingScreen";
import { getDashboardSummary } from "../../services/admin";

const formatCents = (cents) => {
  if (cents === null || cents === undefined) return "—";
  const value = Number(cents) / 100;
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") return value.toLocaleString("pt-BR");
  return value;
};

const formatTime = (isoString) => {
  if (!isoString) return null;
  try {
    const d = new Date(isoString);
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return null;
  }
};

const severityStyles = {
  critical: "bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-rose-300",
  warning: "bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-300",
  info: "bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-300"
};

const severityIcons = {
  critical: <AlertTriangle size={16} />,
  warning: <AlertCircle size={16} />,
  info: <Info size={16} />
};

const ClickableStatCard = ({ title, value, icon, color, route, subtitle }) => {
  const history = useHistory();
  return (
    <div
      onClick={() => route && history.push(route)}
      className={`neumorphic-card rounded-2xl p-5 border transition-all duration-300 hover:border-purple-500/30 hover:scale-[1.01] flex flex-col justify-between ${route ? "cursor-pointer" : ""}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <span className="text-[11px] font-bold tracking-wider uppercase text-gray-500 dark:text-gray-400">
            {title}
          </span>
          <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white mt-1 tracking-tight">
            {value === null || value === undefined ? "—" : value}
          </div>
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-br border ${
          color === "purple" ? "from-purple-500/20 to-purple-600/5 text-purple-600 dark:text-purple-400 border-purple-500/30" :
          color === "pink" ? "from-pink-500/20 to-pink-600/5 text-pink-600 dark:text-pink-400 border-pink-500/30" :
          color === "blue" ? "from-blue-500/20 to-blue-600/5 text-blue-600 dark:text-blue-400 border-blue-500/30" :
          color === "amber" ? "from-amber-500/20 to-amber-600/5 text-amber-600 dark:text-amber-400 border-amber-500/30" :
          color === "green" ? "from-emerald-500/20 to-emerald-600/5 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" :
          "from-purple-500/20 to-purple-600/5 text-purple-600 dark:text-purple-400 border-purple-500/30"
        }`}>
          {icon}
        </div>
      </div>
      {(subtitle || route) && (
        <div className="mt-4 pt-3 border-t border-gray-100 dark:border-white/5 flex items-center justify-between text-xs">
          {subtitle && <span className="text-gray-500 dark:text-gray-400 font-medium">{subtitle}</span>}
          {route && (
            <span className="text-purple-600 dark:text-purple-400 font-semibold flex items-center gap-1">
              Ver detalhes <ArrowRight size={12} />
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getDashboardSummary();
      setSummary(data);
      setLastUpdated(data.generatedAt || new Date().toISOString());
    } catch (err) {
      setError(err.message || "Erro ao carregar dados do dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const data = await getDashboardSummary();
        if (isMounted) {
          setSummary(data);
          setLastUpdated(data.generatedAt || new Date().toISOString());
        }
      } catch (err) {
        if (isMounted) setError(err.message || "Erro ao carregar resumo.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, []);

  if (loading) {
    return <LoadingScreen message="Carregando métricas e indicadores..." />;
  }

  const timeStr = formatTime(lastUpdated);

  return (
    <div className="space-y-8">
      <PageHeader
        breadcrumb="Visão Geral"
        title="Dashboard Executivo"
        subtitle="Visão global da plataforma em tempo real"
        action={
          <div className="flex items-center gap-3">
            {timeStr && (
              <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500">
                Atualizado em {timeStr}
              </span>
            )}
            <button
              onClick={loadSummary}
              className="flex items-center gap-2 px-4 py-2 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 border border-gray-200 dark:border-white/10 text-xs font-semibold text-gray-700 dark:text-gray-200 rounded-xl transition-all"
            >
              <RefreshCw size={14} /> Atualizar
            </button>
          </div>
        }
      />

      {error && (
        <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-300 text-xs font-semibold flex items-center gap-3">
          <AlertTriangle size={18} /> {error}
        </div>
      )}

      {summary && (
        <>
          {/* BLOCO 1 — RESUMO */}
          <div>
            <h2 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Building2 size={16} className="text-purple-600 dark:text-purple-400" /> Resumo da Plataforma
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <ClickableStatCard
                title="Empresas"
                value={formatNumber(summary.companies?.total)}
                icon={<Building2 size={20} />}
                color="purple"
                route="/companies"
                subtitle={`${summary.companies?.active || 0} ativas · ${summary.companies?.overdue || 0} vencidas`}
              />
              <ClickableStatCard
                title="Usuários"
                value={formatNumber(summary.users?.total)}
                icon={<Users size={20} />}
                color="blue"
                route="/users"
                subtitle={summary.users?.superadmins ? `${summary.users.superadmins} superadmins` : undefined}
              />
              <ClickableStatCard
                title="Conexões"
                value={formatNumber(summary.connections?.total)}
                icon={<Wifi size={20} />}
                color="green"
                route="/connections"
                subtitle={`${summary.connections?.connected || 0} online · ${summary.connections?.disconnected || 0} offline`}
              />
              <ClickableStatCard
                title="Assinaturas"
                value={formatNumber(summary.subscriptions?.total)}
                icon={<CreditCard size={20} />}
                color="purple"
                route="/subscriptions"
                subtitle={
                  summary.subscriptions?.active !== null
                    ? `${summary.subscriptions.active} ativas`
                    : "Integração pendente"
                }
              />
            </div>
          </div>

          {/* BLOCO 2 — FINANCEIRO */}
          <div>
            <h2 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <DollarSign size={16} className="text-purple-600 dark:text-purple-400" /> Financeiro
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <ClickableStatCard
                title="Recebido no Mês"
                value={formatCents(summary.revenue?.thisMonthCents)}
                icon={<TrendingUp size={20} />}
                color="green"
                route="/finance"
                subtitle={
                  summary.revenue?.previousMonthCents !== null
                    ? `Mês anterior: ${formatCents(summary.revenue.previousMonthCents)}`
                    : undefined
                }
              />
              <ClickableStatCard
                title="Faturas Abertas"
                value={formatNumber(summary.invoices?.open)}
                icon={<FileText size={20} />}
                color="blue"
                route="/invoices"
              />
              <ClickableStatCard
                title="Faturas Vencidas"
                value={formatNumber(summary.invoices?.overdue)}
                icon={<AlertTriangle size={20} />}
                color="amber"
                route="/invoices"
              />
              <ClickableStatCard
                title="Pagamentos no Mês"
                value={formatNumber(summary.payments?.thisMonthCount)}
                icon={<DollarSign size={20} />}
                color="purple"
                route="/payments"
                subtitle={
                  summary.payments?.thisMonthAmountCents !== null
                    ? formatCents(summary.payments.thisMonthAmountCents)
                    : undefined
                }
              />
            </div>
          </div>

          {/* BLOCO 3 — OPERAÇÃO: CONEXÕES */}
          <div>
            <h2 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Wifi size={16} className="text-purple-600 dark:text-purple-400" /> Conexões — Status
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <ClickableStatCard
                title="Online"
                value={formatNumber(summary.connections?.connected)}
                icon={<Wifi size={20} />}
                color="green"
                route="/connections"
              />
              <ClickableStatCard
                title="Offline"
                value={formatNumber(summary.connections?.disconnected)}
                icon={<WifiOff size={20} />}
                color="pink"
                route="/connections"
              />
              <ClickableStatCard
                title="Atenção"
                value={summary.connections?.attention !== null ? formatNumber(summary.connections.attention) : "—"}
                icon={<AlertCircle size={20} />}
                color="amber"
                route="/connections"
                subtitle={
                  summary.availability?.connections?.attention?.available === false
                    ? "Todos em estado primário"
                    : undefined
                }
              />
            </div>
          </div>

          {/* BLOCO 4 — PLATAFORMA */}
          <div>
            <h2 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Shield size={16} className="text-purple-600 dark:text-purple-400" /> Plataforma & Integrações
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="neumorphic-card rounded-2xl p-5 border">
                <span className="text-[11px] font-bold tracking-wider uppercase text-gray-500 dark:text-gray-400">Asaas</span>
                <div className="mt-2 space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-300">Configurado</span>
                    <span className={`font-semibold ${summary.asaas?.configured ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                      {summary.asaas?.configured === null ? "—" : summary.asaas.configured ? "Sim" : "Não"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-300">Habilitado</span>
                    <span className={`font-semibold ${summary.asaas?.enabled ? "text-emerald-600 dark:text-emerald-400" : "text-gray-400"}`}>
                      {summary.asaas?.enabled === null ? "—" : summary.asaas.enabled ? "Sim" : "Não"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-300">Ambiente</span>
                    <span className="font-semibold text-gray-700 dark:text-gray-200">
                      {summary.asaas?.environment || "—"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="neumorphic-card rounded-2xl p-5 border">
                <span className="text-[11px] font-bold tracking-wider uppercase text-gray-500 dark:text-gray-400">Billing V2</span>
                <div className="mt-2 space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-300">Webhook</span>
                    <span className={`font-semibold ${summary.asaas?.webhookEnabled ? "text-emerald-600 dark:text-emerald-400" : "text-gray-400"}`}>
                      {summary.asaas?.webhookEnabled === null ? "—" : summary.asaas.webhookEnabled ? "Ativo" : "Inativo"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-300">Escrita</span>
                    <span className={`font-semibold ${summary.asaas?.writeEnabled ? "text-emerald-600 dark:text-emerald-400" : "text-gray-400"}`}>
                      {summary.asaas?.writeEnabled === null ? "—" : summary.asaas.writeEnabled ? "Habilitada" : "Desabilitada"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="neumorphic-card rounded-2xl p-5 border">
                <span className="text-[11px] font-bold tracking-wider uppercase text-gray-500 dark:text-gray-400">Assinaturas</span>
                <div className="mt-2 space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-300">Ativas</span>
                    <span className="font-semibold text-gray-700 dark:text-gray-200">{formatNumber(summary.subscriptions?.active)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-300">Trial</span>
                    <span className="font-semibold text-gray-700 dark:text-gray-200">{formatNumber(summary.subscriptions?.trial)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-300">Atraso</span>
                    <span className="font-semibold text-gray-700 dark:text-gray-200">{formatNumber(summary.subscriptions?.pastDue)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-300">Canceladas</span>
                    <span className="font-semibold text-gray-700 dark:text-gray-200">{formatNumber(summary.subscriptions?.cancelled)}</span>
                  </div>
                </div>
              </div>

              <div className="neumorphic-card rounded-2xl p-5 border">
                <span className="text-[11px] font-bold tracking-wider uppercase text-gray-500 dark:text-gray-400">Faturas</span>
                <div className="mt-2 space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-300">Abertas</span>
                    <span className="font-semibold text-gray-700 dark:text-gray-200">{formatNumber(summary.invoices?.open)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-300">Vencidas</span>
                    <span className="font-semibold text-gray-700 dark:text-gray-200">{formatNumber(summary.invoices?.overdue)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-300">Pagas</span>
                    <span className="font-semibold text-gray-700 dark:text-gray-200">{formatNumber(summary.invoices?.paid)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* BLOCO 5 — ALERTAS */}
          <div className="neumorphic-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-900 dark:text-gray-200 flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-500 dark:text-amber-400" /> Alertas Executivos
              </h3>
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-300">
                {summary.alerts?.length || 0} Alerta(s)
              </span>
            </div>
            {summary.alerts && summary.alerts.length > 0 ? (
              <div className="space-y-2 mt-3">
                {summary.alerts.map((alert, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-xl border text-xs flex items-center justify-between ${
                      severityStyles[alert.severity] || severityStyles.info
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {severityIcons[alert.severity] || severityIcons.info}
                      <span>
                        <span className="font-semibold">{alert.title}</span>
                        {alert.message && <span className="ml-2 opacity-80">{alert.message}</span>}
                      </span>
                    </div>
                    <span className="text-[10px] font-mono opacity-60">{alert.code}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Nenhum alerta operacional no momento. A plataforma está operando normalmente.
              </p>
            )}
          </div>

          {/* BLOCO 6 — EMPRESAS QUE PRECISAM DE ATENÇÃO */}
          {summary.attentionCompanies && summary.attentionCompanies.length > 0 && (
            <div className="neumorphic-card rounded-2xl p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-200 flex items-center gap-2">
                  <AlertCircle size={18} className="text-amber-500 dark:text-amber-400" /> Empresas que Precisam de Atenção
                </h3>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-300">
                  {summary.attentionCompanies.length}
                </span>
              </div>
              <div className="space-y-2 mt-3">
                {summary.attentionCompanies.map((item) => (
                  <div
                    key={item.companyId}
                    className="p-3 rounded-xl bg-gray-50 dark:bg-white/5 text-xs flex items-center justify-between"
                  >
                    <div>
                      <span className="font-semibold text-gray-900 dark:text-white">{item.companyName}</span>
                      <div className="flex gap-1.5 mt-1 flex-wrap">
                        {item.reasons.map((reason) => (
                          <span
                            key={reason}
                            className="px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-300 font-medium"
                          >
                            {reason === "company_overdue" && "Vencida"}
                            {reason === "subscription_past_due" && "Assinatura atraso"}
                            {reason === "invoice_overdue" && "Fatura vencida"}
                            {reason === "connection_disconnected" && "Conexão offline"}
                            {!["company_overdue", "subscription_past_due", "invoice_overdue", "connection_disconnected"].includes(reason) && reason}
                          </span>
                        ))}
                      </div>
                    </div>
                    <ExternalLink size={14} className="text-gray-400 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Availability notice */}
          {summary.availability && Object.values(summary.availability).some(v => v && v.available === false) && (
            <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-300 text-xs flex items-center gap-3">
              <Info size={18} />
              <span>
                Algumas métricas podem estar indisponíveis. Dados parciais são exibidos quando disponíveis.
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Dashboard;
